import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import type {
  IETLConfigurationsResponse,
  IETLCompleteSetupRequest,
  IETLCompleteSetupResponse,
  IETLStatusResponse,
} from '../../interface/etlDashboard.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { etlService } from '../../services/etlDashboard';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

/**
 * Async Thunk: Fetch ETL Configurations
 * GET /api/v2/etl/configurations
 */
export const fetchETLConfigurations = createAsyncThunk<
  IETLConfigurationsResponse,
  void,
  { rejectValue: IErrorResponse }
>('etl/fetch-configurations', async (_, { rejectWithValue }) => {
  try {
    const response = await etlService.fetchETLConfigurations();
    return response.data;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(
        err,
        i18n.t('toast.failed_to_fetch_etl_configurations', 'Failed to fetch ETL configurations.'),
      ),
    );
  }
});

/**
 * Async Thunk: Complete ETL Setup
 * PATCH /api/v2/etl/complete
 * No longer returns tokens — just completion status
 */
export const completeETLSetup = createAsyncThunk<
  IETLCompleteSetupResponse,
  IETLCompleteSetupRequest,
  { rejectValue: IErrorResponse }
>('etl/complete-setup', async (payload, { rejectWithValue }) => {
  try {
    const response = await etlService.completeETLSetup(payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(
        err,
        i18n.t('toast.failed_to_complete_etl_setup', 'Failed to complete ETL setup.'),
      ),
    );
  }
});

/**
 * Async Thunk: Fetch ETL Status (lightweight - paywall banner flag)
 * GET /api/v2/etl/status
 * Server writes IS_VIEW_ETL_PAYWALL_BANNER preference atomically on first call.
 */
export const fetchETLStatus = createAsyncThunk<
  IETLStatusResponse,
  void,
  { rejectValue: IErrorResponse }
>('etl/fetch-status', async (_, { rejectWithValue }) => {
  try {
    const response = await etlService.getETLStatus();
    return response.data;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_fetch_etl_status', 'Failed to fetch ETL status.')),
    );
  }
});

/**
 * Export all ETL actions as a grouped object
 */
export const etlActions = {
  fetchETLConfigurations,
  completeETLSetup,
  fetchETLStatus,
};
